import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';

import { UnrealBloomPass } from 'three/examples/jsm/Addons.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { GammaCorrectionShader } from 'three/examples/jsm/shaders/GammaCorrectionShader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { ACESFilmicToneMappingShader } from 'three/examples/jsm/Addons.js';
import * as dat from 'lil-gui';


// Base
const gui = new dat.GUI();
gui.hide();
const canvas = document.querySelector('canvas.webgl');
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x000000);

const options = {
  enableSwoopingCamera: false,
  enableRotation: false,
  color: 0xffffff,
  metalness: 0,
  roughness: 0.2,
  transmission: 1,
  ior: 1.5,
  reflectivity: 0.5,
  thickness: 2.5,
  envMapIntensity: 1.5,
  clearcoat: 1,
  clearcoatRoughness: 0.1,
  normalScale: 0,
  clearcoatNormalScale: 0.2,
  normalRepeat: 4,
  // attenuationTint: 0xffffff,
  // attenuationDistance: 0,
  bloomThreshold: 0.85,
  bloomStrength: 0.35,
  bloomRadius: 0.33,
  opacity: .48,
};


const hdrEquirect = new RGBELoader().load(
  "/sky.hdr",
  () => {
    hdrEquirect.mapping = THREE.EquirectangularReflectionMapping;
  }
);

const textureLoader = new THREE.TextureLoader();



const normalMapTexture = textureLoader.load("/normal.jpg");
normalMapTexture.wrapS = THREE.RepeatWrapping;
normalMapTexture.wrapT = THREE.RepeatWrapping;
normalMapTexture.repeat.set(options.normalRepeat, options.normalRepeat);

function isMobileDevice() {
    return (typeof window.orientation !== "undefined") || (navigator.userAgent.indexOf('IEMobile') !== -1);
}

const material = new THREE.MeshPhysicalMaterial({
  color: 0xffffff,
  metalness: options.metalness,
  roughness: options.roughness,
  transmission: options.transmission,
  ior: options.ior,
  reflectivity: options.reflectivity,
  thickness: options.thickness,
  envMap: hdrEquirect, // Set envMap to null to remove reflection
  envMapIntensity: options.envMapIntensity,
  clearcoat: options.clearcoat,
  clearcoatRoughness: options.clearcoatRoughness,
  // normalScale: new THREE.Vector2(options.normalScale),
  // normalMap: normalMapTexture,
  opacity: options.opacity, 
  transparent: true, // Enable transparency
  // clearcoatNormalMap: normalMapTexture,
  // clearcoatNormalScale: new THREE.Vector2(options.clearcoatNormalScale),
});

// material.depthWrite = false;
material.transparent = true;

function findGeometry(model) {
  let foundGeometry = null;

  model.traverse((node) => {
    if (node.isMesh && node.geometry) {
      foundGeometry = node.geometry.clone();
      return; // Stop traversing once we find the first geometry
    }
  });

  return foundGeometry;
}



const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("/draco/");
dracoLoader.setDecoderConfig({ type: 'js' });

const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

gltfLoader.load("/glass1.glb", (gltf) => {
  const model = gltf.scene;

  // Find any geometry in the model
  const glassGeometry = findGeometry(model);
  glassGeometry.rotateX(Math.PI / -2);
  glassGeometry.translate(0, -1, 0);



  if (glassGeometry) {
    const glassMesh = new THREE.Mesh(glassGeometry, material);
    if (isMobileDevice()) {
        glassMesh.scale.set(0.015, 0.015, 0.015);
        glassMesh.position.set(0, -.7, 0); 
    } else {
        glassMesh.scale.set(0.023, 0.023, 0.023);
        glassMesh.position.set(0, -1, 0); 
    }
// Adjust position as needed
    scene.add(glassMesh);
  } else {
    console.error("Failed to find geometry in GLB model.");
  }

  // Dispose of the original model to free up resources
  model.traverse((node) => {
    if (node.isMesh) {
      node.geometry.dispose();
      node.material.dispose();
    }
  });

}, undefined, (error) => {
  console.error(error);
});


//CAMERA
const camera = new THREE.PerspectiveCamera(35, window.innerWidth / window.innerHeight, 0.1, 15);
camera.position.set(5, 0, -0.05);
scene.add(camera);


// Sizes
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
};
window.addEventListener('resize', () => {
    sizes.width = window.innerWidth;
    sizes.height = window.innerHeight;
    camera.aspect = sizes.width / sizes.height;
    camera.updateProjectionMatrix();
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    effectComposer.setSize(sizes.width, sizes.height);
    effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});




const controls = new TrackballControls(camera, canvas);

controls.enableDamping = true; 
controls.noZoom = true;
controls.noPan = true;

controls.mouseButtons = {
    LEFT: THREE.MOUSE.ROTATE,
    MIDDLE: THREE.MOUSE.ROTATE, 
    RIGHT: THREE.MOUSE.ROTATE, 
};
controls.rotateSpeed = 0.8;

//////////////////////////////////////////////////
//// ON MOUSE MOVE TO GET CAMERA POSITION
// document.addEventListener('mousemove', (event) => {
//     event.preventDefault()

//     console.log(camera.position)

// }, false)


// Renderer
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
});
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFShadowMap;
renderer.physicallyCorrectLights = true;
renderer.outputEncoding = THREE.sRGBEncoding;
renderer.toneMapping = THREE.ReinhardToneMapping;
renderer.toneMappingExposure = 1.5;
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

// Post processing
const renderTarget = new THREE.WebGLRenderTarget(800, 600, { samples: 2 });
const effectComposer = new EffectComposer(renderer, renderTarget);
effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
effectComposer.setSize(sizes.width, sizes.height);

// Render pass
const renderPass = new RenderPass(scene, camera);
effectComposer.addPass(renderPass);



// ACESFILMIC  pass
const ACESFilmShiftPass = new ShaderPass(ACESFilmicToneMappingShader);
ACESFilmShiftPass.enabled = true;
effectComposer.addPass(ACESFilmShiftPass);

//bloom pass 
const bloomPass = new UnrealBloomPass(
  
  new THREE.Vector2(sizes.width, sizes.height),
  options.bloomStrength,
  options.bloomRadius,
  options.bloomThreshold
);
effectComposer.addPass(bloomPass);


// Gamma correction pass
const gammaCorrectionPass = new ShaderPass(GammaCorrectionShader);
effectComposer.addPass(gammaCorrectionPass);

// Antialias pass
if(renderer.getPixelRatio() === 1 && !renderer.capabilities.isWebGL2) {
    const smaaPass = new SMAAPass();
    effectComposer.addPass(smaaPass);
}

gui.addColor(options, "color").onChange((val) => {
  material.color.set(val);
});

gui.add(options, "roughness", 0, 1, 0.01).onChange((val) => {
  material.roughness = val;
});
gui.add(options, "opacity", 0, 1, 0.01).onChange((val) => {
  material.roughness = val;
});
gui.add(options, "metalness", 0, 1, 0.01).onChange((val) => {
  material.metalness = val;
});

gui.add(options, "transmission", 0, 1, 0.01).onChange((val) => {
  material.transmission = val;
});

gui.add(options, "ior", 1, 2.33, 0.01).onChange((val) => {
  material.ior = val;
});

gui.add(options, "reflectivity", 0, 4, 0.01).onChange((val) => {
  material.reflectivity = val;
});

gui.add(options, "thickness", 0, 5, 0.1).onChange((val) => {
  material.thickness = val;
});

gui.add(options, "clearcoat", 0, 1, 0.01).onChange((val) => {
  material.clearcoat = val;
});
gui.add(options, "envMapIntensity", 0, 20, 0.1).onChange((val) => {
  material.envMapIntensity = val;
});

gui.add(options, "clearcoatRoughness", 0, 1, 0.01).onChange((val) => {
  material.clearcoatRoughness = val;
});

gui.add(options, "normalScale", 0, 5, 0.01).onChange((val) => {
  material.normalScale.set(val, val);
});

gui.add(options, "clearcoatNormalScale", 0, 5, 0.01).onChange((val) => {
  material.clearcoatNormalScale.set(val, val);
});

gui.add(options, "normalRepeat", 1, 4, 1).onChange((val) => {
  normalMapTexture.repeat.set(val, val);
});

// gui.addColor(options, "attenuationTint").onChange((val) => {
//   material.attenuationTint.set(val);
// });

// gui.add(options, "attenuationDistance", 0, 1, 0.01).onChange((val) => {
//   material.attenuationDistance = val;
// });

const postprocessing = gui.addFolder("Post Processing");

postprocessing.add(options, "bloomThreshold", 0, 1, 0.01).onChange((val) => {
  bloomPass.threshold = val;
});

postprocessing.add(options, "bloomStrength", 0, 5, 0.01).onChange((val) => {
  bloomPass.strength = val;
});

postprocessing.add(options, "bloomRadius", 0, 1, 0.01).onChange((val) => {
  bloomPass.radius = val;
});



// Animate
const clock = new THREE.Clock();
const tick = () => {
    const elapsedTime = clock.getElapsedTime();
    controls.update();
    effectComposer.render();
    window.requestAnimationFrame(tick);
};

tick();

// Function to update all materials
const updateAllMaterials = () => {
    scene.traverse((child) => {
        if(child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
            child.material.envMapIntensity = 1.5;
            child.material.needsUpdate = true;
            child.castShadow = true;
            child.receiveShadow = true;
        }
    });
};